import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, defer, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { EligibilityService } from '@core';
import { AppConstants } from '../../../app.constants';
import { LoaderService } from '@management';
import { UpdateCoverageDetailsDialogComponent } from './update-coverage-details-dialog/update-coverage-details-dialog.component';
import {
  IFlexTagTypes,
  MemberRxHistoryResponseInterface,
  RxTypeInterface,
  Coverage,
  CoverageResponse,
  MemberDetails,
} from '@interfaces';
import { SharedService, AppCenterAnalytics, AppInsightsService } from '@services';

@Component({
  selector: 'app-update-coverage',
  templateUrl: './update-coverage.component.html',
  styleUrls: ['./update-coverage.component.scss'],
  standalone: false
})
export class UpdateCoverageComponent implements OnInit, OnDestroy {
  public displayedColumns: string[] = [
    'group',
    'client',
    'effectiveDate',
    'endDate',
    'edit',
  ];
  public displayedColumnsRx: [string, string, string, string] = [
    'memberRxType',
    'effectiveDate',
    'endDate',
    'edit',
  ];
  public selectedTab: 'active' | 'future' | 'all' = 'active';
  public dataSource!: MatTableDataSource<Coverage>;
  public coverage: Coverage[] = [];
  public dataSourceRx!: MatTableDataSource<RxTypeInterface>;
  public coverageDetails$!: Observable<CoverageResponse>;
  public memberRxDetails$!: Observable<
    Partial<MemberRxHistoryResponseInterface>
  >;
  public selectedMemberDetails$: Observable<Partial<MemberDetails>>;
  public memberDetails: MemberDetails;
  public displayGender = AppConstants.DISPLAY_GENDER;
  public displayCoverageStatus = AppConstants.DISPLAY_COVERAGE_STATUS;
  public displayCoverageType = AppConstants.DISPLAY_COVERAGE_TYPE;
  public isActiveCoverage = false;
  public availableFlexTag: IFlexTagTypes[] = [];
  public coverageTypes = AppConstants.COVERAGE_TYPES;
  public cardHolderGroupNumber: string;
  // allEndDatesArray: string[] = [];
  public relationShip = AppConstants.RELATIONSHIP_VALUES;
  cardHolderRC: string = AppConstants.RELATIONSHIP_CODES_ALL.CARDHOLDER;
  private subscription: Subscription[] = [];
  public isActive = true;
  public cardHoldersFutureCoverage: Coverage;
  public cardHoldersCurrentCoverage: Coverage;
  constructor(
    private readonly router: Router,
    private matDialog: MatDialog,
    private readonly appInsightsService: AppInsightsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly eligibilityService: EligibilityService,
    private readonly loader: LoaderService,
    private sharedService: SharedService
  ) { }

  ngOnInit(): void {
    this.loader.showLoader();
    this.selectedMemberDetails$ = this.eligibilityService.searchCoverage.pipe(
      switchMap(() => this.activatedRoute.queryParams.pipe(

        switchMap((params) => {
          this.cardHolderGroupNumber = params['cardHolderRCGrpNum'] ? params['cardHolderRCGrpNum'] : '';
          return this.eligibilityService.getMemberDetailsById(params).pipe(
            switchMap((members) => {
              this.memberDetails = members[0];
              this.getAvailableFlexTag(this.memberDetails.clientId);
              this.getMembersCoverageDetails();
              return of(members[0])
            }));
        })
      )));
    this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_COVERAGE);
  }

  private getMembersCoverageDetails(): void {
    this.coverageDetails$ = this.geCoverageDetails().pipe(
      tap((res) => {
        this.coverage = res?.coverage ?? [];
        const displayData = this.filteroutActiveCoverageRecords();
        this.coverage.sort(this.sortByActiveStatus);
        this.dataSource = new MatTableDataSource(displayData);
        this.getMemberRxCoverageList();
        if (this.memberDetails.relationshipCode === this.cardHolderRC) {
          this.cardHoldersFutureCoverage = res?.coverage?.find((c) => c.eligibilityIndicator === 'F' && c.active === 'False') ?? null;
        }
      }),

      switchMap((res) => {
        // Conditionally call API if NOT card holder
        if (this.memberDetails.relationshipCode !== this.cardHolderRC) {
          this.loader.showLoader();
          return this.eligibilityService
            .getCoverageDetails(
              this.memberDetails.clientId,
              this.memberDetails.cardId ?? '',
              '01',
              this.memberDetails.empirxMemberId ?? ''
            )
            .pipe(
              take(1),
              tap((res) => {
                this.loader.hideLoader();
                this.cardHoldersFutureCoverage =
                  res?.coverage?.find(
                    (c) => c.eligibilityIndicator === 'F' && c.active === 'False'
                  ) ?? null;
                this.cardHoldersCurrentCoverage =
                res?.coverage?.find(
                  (c) => c.eligibilityIndicator === 'C'
                ) ?? null;  
              }),
            );
        }
        this.loader.hideLoader();
        // If no API call needed, return EMPTY observable
        return of(res);

      }),
    );
  }

  private filteroutActiveCoverageRecords(): Coverage[] {
    const displayData = this.isActive
      ? this.coverage.filter((cov) => cov.eligibilityIndicator === 'C')
      : this.selectedTab === 'future'
        ? this.coverage.filter((cov) => cov.eligibilityIndicator === 'F')
        : this.coverage;

    const filtered = this.coverage.filter(
      (cov) => new Date(cov.endDate).valueOf() >= new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf() &&
        cov.active === 'True'
    );

    if (!this.cardHolderGroupNumber) {
      this.cardHolderGroupNumber =
        this.coverage.filter((c) => c.active)[0].cardholder_group_number;
    }

    this.isActiveCoverage = filtered.length !== 0;
    return displayData;
  }

  public onTabClick(tabName: 'active' | 'future' | 'all'): void {
    this.selectedTab = tabName;

    const commonColumns = [
      'group',
      'client',
      'effectiveDate',
      'endDate',
      'edit'
    ];

    let displayData = this.coverage;
    this.isActive = false;

    switch (tabName) {
      case 'active':
        displayData = this.coverage.filter(cov => cov.eligibilityIndicator === 'C');
        this.isActive = true;
        this.displayedColumns = commonColumns;
        break;

      case 'future':
        displayData = this.coverage.filter(cov => cov.eligibilityIndicator === 'F');
        this.displayedColumns = commonColumns;
        break;

      case 'all':
        this.displayedColumns = [...commonColumns.slice(0, 4), 'status', 'edit'];
        break;
    }

    this.dataSource = new MatTableDataSource(displayData);
  }

  get isLevelD(): boolean {
    return this.sharedService.isLevelD();
  }

  getAvailableFlexTag(clientId: string): void {
    this.subscription.push(this.eligibilityService.getFlexTagTypes(clientId).pipe(
      tap((tags) => {
        this.availableFlexTag = tags
        if (this.memberDetails.relationshipCode !== AppConstants.RELATIONSHIP_CODES_ALL.CARDHOLDER) {
          const index = this.availableFlexTag.findIndex((e) => e.Tag === 'FORCE65');
          if (index !== -1) {
            this.availableFlexTag.splice(index, 1);
          }
        }
      })
    ).subscribe());
  }

  getMemberRxCoverageList() {
    this.memberRxDetails$ = defer(() => {
      return this.eligibilityService
        .getRxCoverageDetails({
          patientId: this.memberDetails.patientId,
          clientId: this.memberDetails.clientId,
          cardId: this.memberDetails.cardId ?? '',
          empirxMemberId: this.memberDetails.empirxMemberId ?? ''
        })
        .pipe(
          tap((res) => {
            let sortedResponseArray = res.rxType.sort(this.sortByActiveStatus);
            sortedResponseArray = sortedResponseArray.filter((e) => e.flexTagName && e.flexTagName !== 'None')
            this.dataSourceRx = new MatTableDataSource(sortedResponseArray);
          }),
          catchError((err) => {
            this.loader.hideLoader();
            return of({});
          })
        )
    });
  }

  sortByActiveStatus = (a: any, b: any): number => {
    const isActiveA = a.active.toLowerCase() === 'true';
    const isActiveB = b.active.toLowerCase() === 'true';

    // Sort in descending order so that "True" comes first
    if (isActiveA && !isActiveB) {
      return -1;
    } else if (!isActiveA && isActiveB) {
      return 1;
    } else {
      return 0;
    }
  };

  allDatesInThePast(dateStringArray: string[]): boolean {
    const today = new Date();

    return dateStringArray.every((dateString) => {
      const date = new Date(dateString);
      return date < today;
    });
  }

  dateInThePast(dateString: string): boolean {
    const givenDate = new Date(dateString);

    const today = new Date();

    if (givenDate < today) {
      return true;
    } else if (givenDate > today) {
      return false;
    } else {
      return false;
    }
  }

  public navigateBackToMemberSearch(): void {
    this.router.navigateByUrl('/management/online-eligibility');
  }

  public addNewCoverage(action: string): void {
    if (this.memberDetails.relationshipCode !== AppConstants.RELATIONSHIP_CODES_ALL.CARDHOLDER) {
      const index = this.availableFlexTag.findIndex((e) => e.Tag === 'FORCE65');
      if (index !== -1) {
        this.availableFlexTag.splice(index, 1);
      }
    }
    const coverage = { edit: false };
    this.appInsightsService.trackPageView(AppCenterAnalytics.UPDATE_COVERAGE_DIALOG_OPENED, { Action: action });
    const dialogRef: MatDialogRef<UpdateCoverageDetailsDialogComponent> =
      this.matDialog.open(UpdateCoverageDetailsDialogComponent, {
        autoFocus: false,
        minWidth: window.innerWidth < 766 ? '600' : '980',
        panelClass: (action === 'addCoverage' || action === 'addFutureCoverage') && this.memberDetails.relationshipCode === this.cardHolderRC ? 'update-coverage-details-modalbox' : 'add-coverage-types-modalbox',
        data: {
          coverage,
          action,
          memberDetails: this.memberDetails,
          flexTagTypes: this.availableFlexTag,
          cardHolderGroupNumber: this.cardHolderGroupNumber,
          cardHoldersFutureCoverage: this.cardHoldersFutureCoverage
        },
      });
    dialogRef.afterClosed().subscribe(() => {
    });
  }

  public geCoverageDetails(): Observable<CoverageResponse> {
    return defer(() => {
      this.loader.showLoader();
      const isCardHolderGroupNumberRequired = this.cardHolderGroupNumber ? '' : 'true';
      return this.eligibilityService
        .getCoverageDetails(
          this.memberDetails.clientId,
          this.memberDetails.cardId ?? '',
          this.memberDetails.personCode ?? '',
          this.memberDetails.empirxMemberId ?? '',
          isCardHolderGroupNumberRequired
        )
        .pipe(
          catchError((err) => {
            this.loader.hideLoader();
            return of({ count: 0, coverage: [] });
          })
        )
    });
  }

  public updateCoverage(action: string, coverage: Coverage): void {
    coverage.edit = true;
    const dialogRef: MatDialogRef<UpdateCoverageDetailsDialogComponent> =
      this.matDialog.open(UpdateCoverageDetailsDialogComponent, {
        autoFocus: false,
        minWidth: window.innerWidth < 766 ? 'auto' : 980,
        maxHeight: 500,
        data: { coverage, action, memberDetails: this.memberDetails, flexTagTypes: this.availableFlexTag },
      });
    dialogRef.afterClosed().subscribe(() => {
    });
  }

  
  public hasInfiniteCoverage(): boolean {
    return new Date(this.memberDetails.coverage?.endDate).valueOf() === new Date('9999-12-31').valueOf();
  }

  public hasFutureCoverage(): boolean {
    return this.coverage?.filter(c=> c.eligibilityIndicator === 'F' && c.active === 'False').length > 0
  }

  public hasFutureEligibleCoverage(): boolean {
    return this.coverage?.filter(c=> c.eligibilityIndicator === 'F' && c.active === 'True').length> 0;
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.forEach((s) => s.unsubscribe());
  }
}
